import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { paymentApi } from "@/lib/api";
import { Loader2, CheckCircle2, Clock, AlertTriangle, ArrowRight } from "lucide-react";

const MAX_POLLS = 20;
const INTERVAL_MS = 2000;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const appId = params.get("app");
  const [state, setState] = useState("checking"); // checking | paid | processing | timeout | error
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    let timer;

    const poll = async () => {
      try {
        const { data } = await paymentApi.status(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.payment_status === "processing") {
          setState("processing");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          setState("timeout");
          return;
        }
        timer = setTimeout(poll, INTERVAL_MS);
      } catch {
        if (cancelled) return;
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          setState("timeout");
          return;
        }
        timer = setTimeout(poll, INTERVAL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  const goToApp = () => navigate(appId ? `/applications/${appId}` : "/dashboard");

  return (
    <div className="wechr-section">
      <div className="wechr-container flex justify-center">
        <Card className="w-full max-w-lg" data-testid="payment-success-card">
          <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
            {state === "checking" && (
              <>
                <Loader2 className="h-10 w-10 animate-spin text-wgreen-600" />
                <h1 className="font-heading text-xl font-semibold text-navy-900">Confirming your payment…</h1>
                <p className="max-w-sm text-sm text-[var(--wechr-muted-text)]">Please wait a moment while we verify your payment with our processor.</p>
              </>
            )}
            {state === "paid" && (
              <>
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-wgreen-100"><CheckCircle2 className="h-8 w-8 text-wgreen-700" /></div>
                <h1 className="font-heading text-xl font-semibold text-navy-900" data-testid="payment-success-title">Payment received</h1>
                <p className="max-w-sm text-sm text-[var(--wechr-muted-text)]">Thank you. Your application is now marked <strong>Paid / Ready for Review</strong> and our team will begin the document review.</p>
                <Button className="btn-green" onClick={goToApp} data-testid="payment-success-continue">View application <ArrowRight className="ml-1 h-4 w-4" /></Button>
              </>
            )}
            {state === "processing" && (
              <>
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gold-100"><Clock className="h-8 w-8 text-gold-700" /></div>
                <h1 className="font-heading text-xl font-semibold text-navy-900">Bank payment processing</h1>
                <p className="max-w-sm text-sm text-[var(--wechr-muted-text)]">Your bank (ACH) payment has been submitted and is settling. This can take a few business days. We'll email you and start the review as soon as it clears.</p>
                <Button className="btn-green" onClick={goToApp}>Back to application <ArrowRight className="ml-1 h-4 w-4" /></Button>
              </>
            )}
            {state === "timeout" && (
              <>
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[var(--wechr-subtle)]"><Clock className="h-8 w-8 text-navy-700" /></div>
                <h1 className="font-heading text-xl font-semibold text-navy-900">Still confirming</h1>
                <p className="max-w-sm text-sm text-[var(--wechr-muted-text)]">Your payment is taking a little longer to confirm. You can safely return to your application — the status will update automatically.</p>
                <Button className="btn-green" onClick={goToApp}>Back to application <ArrowRight className="ml-1 h-4 w-4" /></Button>
              </>
            )}
            {state === "error" && (
              <>
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FEE4E2]"><AlertTriangle className="h-8 w-8 text-[#B42318]" /></div>
                <h1 className="font-heading text-xl font-semibold text-navy-900">We couldn't confirm this payment</h1>
                <p className="max-w-sm text-sm text-[var(--wechr-muted-text)]">Something went wrong confirming your payment. If you were charged, please contact us — otherwise you can try again from your application.</p>
                <div className="flex gap-2">
                  <Button className="btn-green" onClick={goToApp}>Back to application</Button>
                  <Link to="/contact"><Button variant="outline">Contact support</Button></Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;
